export type LegalDraftKind = 'letter' | 'memo' | 'draft';

export interface LegalDraftArtifact {
  id: string;
  messageIndex: number;
  kind: LegalDraftKind;
  title: string;
  text: string;
  createdAt?: number;
}

export interface LegalDraftFieldValue {
  placeholder: string;
  value: string;
}

export interface LegalDraftSessionState {
  versions: string[];
  currentVersionIndex: number;
  fields: LegalDraftFieldValue[];
  versionComparisons?: Record<string, string>;
  updatedAt?: number;
}

interface TaggedLegalDraft {
  kind: LegalDraftKind;
  text: string;
  title?: string;
  rawText: string;
}

const LEGAL_DRAFT_TAG_PATTERN = /<\/?(artifact|letter|memo|memorandum|draft|legal-draft|legal_draft|document)\b[^>]*>/gi;
const LEGAL_DRAFT_OPEN_TAG_PATTERN = /<(artifact|letter|memo|memorandum|draft|legal-draft|legal_draft|document)\b([^>]*)>/i;
const RETIRED_ARTIFACT_TITLE_PATTERN = /\bwork\s*bench\b/i;

function normalizeDraftText(text: string): string {
  return (text || '').replace(/\\n/g, '\n').trim();
}

function hashDraftText(text: string): string {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function getTagAttribute(attrs: string, name: string): string | undefined {
  const attrPattern = new RegExp(`${name}=["']([^"']+)["']`, 'i');
  return attrs.match(attrPattern)?.[1]?.trim();
}

function getKindFromTag(tagName: string, attrs: string): LegalDraftKind {
  const normalizedTag = tagName.toLowerCase();
  const type = getTagAttribute(attrs, 'type')?.toLowerCase();

  if (type === 'letter' || type === 'demand-letter' || normalizedTag === 'letter') return 'letter';
  if (type === 'memo' || type === 'memorandum' || normalizedTag === 'memo' || normalizedTag === 'memorandum') return 'memo';
  return 'draft';
}

function isChatSocReferenceLabel(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return normalized === 'soc'
    || normalized.startsWith('soc ')
    || normalized === 'chatsoc'
    || normalized.startsWith('chatsoc ')
    || normalized === 'sullivan on comp'
    || normalized.startsWith('sullivan on comp ');
}

function removeChatSocMarkdownLinks(text: string): string {
  const removalRanges: Array<{ start: number; end: number }> = [];
  const openBrackets: number[] = [];
  let index = 0;

  while (index < text.length) {
    if (text[index] === '\n') {
      openBrackets.length = 0;
      index += 1;
      continue;
    }

    if (text[index] === '[') {
      openBrackets.push(index);
      index += 1;
      continue;
    }

    if (
      text[index] === ']'
      && text[index + 1] === '('
      && openBrackets.length > 0
    ) {
      const linkStart = openBrackets.pop() as number;
      const urlEnd = text.indexOf(')', index + 2);
      if (urlEnd === -1) break;

      const label = text.slice(linkStart + 1, index);
      if (isChatSocReferenceLabel(label)) {
        while (
          removalRanges.length > 0
          && removalRanges[removalRanges.length - 1].start >= linkStart
        ) {
          removalRanges.pop();
        }
        removalRanges.push({ start: linkStart, end: urlEnd + 1 });
      }
      index = urlEnd + 1;
      continue;
    }

    index += 1;
  }

  if (removalRanges.length === 0) return text;

  const output: string[] = [];
  let cursor = 0;
  removalRanges.forEach(({ start, end }) => {
    output.push(text.slice(cursor, start));
    cursor = end;
  });
  output.push(text.slice(cursor));
  return output.join('');
}

export function stripLegalDraftTags(text: string): string {
  return normalizeDraftText(text).replace(LEGAL_DRAFT_TAG_PATTERN, '').trim();
}

export function removeChatSocReferences(text: string): string {
  const withoutLinks = removeChatSocMarkdownLinks((text || '').replace(/\\n/g, '\n'));
  return withoutLinks
    .replace(/[\u3010\uFF3B[]((?:\d+(?:,\s*\d+)*))[\u3011\uFF3D\]]/g, '')
    .replace(/[ \t]*\[[ \t]*(?:,[ \t]*)*\]/g, '')
    .replace(/[ \t]+([,.;:])/g, '$1')
    .replace(/,([ \t]*)$/gm, '$1')
    .replace(/,([ \t]*[.;:])/g, '$1')
    .replace(/,[ \t]*,/g, ',')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+$/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
}

export function fieldsFromPlaceholderValues(values: Record<string, string>): LegalDraftFieldValue[] {
  return Object.entries(values)
    .filter(([placeholder]) => placeholder.trim())
    .map(([placeholder, value]) => ({ placeholder, value }));
}

export function placeholderValuesFromFields(fields?: LegalDraftFieldValue[] | null): Record<string, string> {
  if (!Array.isArray(fields)) return {};

  return fields.reduce<Record<string, string>>((values, field) => {
    if (!field?.placeholder) return values;
    values[field.placeholder] = field.value || '';
    return values;
  }, {});
}

export function extractTaggedLegalDraft(text: string): TaggedLegalDraft | null {
  const normalized = normalizeDraftText(text);
  const openTag = normalized.match(LEGAL_DRAFT_OPEN_TAG_PATTERN);
  if (!openTag || openTag.index === undefined) return null;

  const [rawOpenTag, tagName, attrs = ''] = openTag;
  const contentStart = openTag.index + rawOpenTag.length;
  const rest = normalized.slice(contentStart);
  const closeTagPattern = new RegExp(`</${tagName.replace('-', '[-_]')}>`, 'i');
  const closeTag = rest.match(closeTagPattern);
  const content = closeTag && closeTag.index !== undefined
    ? rest.slice(0, closeTag.index)
    : rest;
  const rawText = closeTag && closeTag.index !== undefined
    ? normalized.slice(openTag.index, contentStart + closeTag.index + closeTag[0].length)
    : normalized.slice(openTag.index);

  return {
    kind: getKindFromTag(tagName, attrs),
    title: getTagAttribute(attrs, 'title'),
    rawText,
    text: stripLegalDraftTags(content),
  };
}

function extractHeuristicLegalDraft(text: string): TaggedLegalDraft | null {
  const normalized = normalizeDraftText(text);
  if (!normalized) return null;

  const disclaimerMatch = normalized.match(/\[AI-Generated Document:[\s\S]*?\]/i);
  const memoMatch = normalized.match(/^\s*(MEMORANDUM|MEMO)\b/im);
  const letterMatch = normalized.match(/^\s*(\[DATE\]|VIA\s+\[|Dear\s+.+,)/im);
  const startIndex = disclaimerMatch?.index ?? memoMatch?.index ?? letterMatch?.index;
  if (startIndex === undefined) return null;

  const candidate = normalized.slice(startIndex);
  const notesMatch = candidate.match(/\n\s*(A few practical notes:|Practical notes:|A few notes:|Notes:|Key considerations:|Additional considerations:)\s*/i);
  const rawText = notesMatch && notesMatch.index !== undefined
    ? candidate.slice(0, notesMatch.index).trim()
    : candidate.trim();

  if (!rawText) return null;

  const kind: LegalDraftKind =
    /^\s*(MEMORANDUM|MEMO)\b/im.test(rawText) || (/^\s*TO:\s+/im.test(rawText) && /^\s*RE:\s+/im.test(rawText))
      ? 'memo'
      : (/^\s*(\[DATE\]|VIA\s+\[|Dear\s+.+,)/im.test(rawText) || /\b(Sincerely|Very truly yours|Respectfully submitted),?\s*$/im.test(rawText))
        ? 'letter'
        : 'draft';
  return {
    kind,
    rawText,
    text: stripLegalDraftTags(rawText),
  };
}

export function extractLegalDraft(text: string): TaggedLegalDraft | null {
  return extractTaggedLegalDraft(text) || extractHeuristicLegalDraft(text);
}

export function removeLegalDraftContent(text: string): string {
  const normalized = normalizeDraftText(text);
  const draft = extractLegalDraft(normalized);
  if (!draft) return normalized;

  return normalized
    .replace(draft.rawText, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function splitLegalDraftContent(text: string): { before: string; after: string } {
  const normalized = normalizeDraftText(text);
  const draft = extractLegalDraft(normalized);
  if (!draft) return { before: normalized, after: '' };

  const draftIndex = normalized.indexOf(draft.rawText);
  if (draftIndex === -1) return { before: removeLegalDraftContent(normalized), after: '' };

  return {
    before: normalized.slice(0, draftIndex).trim(),
    after: normalized.slice(draftIndex + draft.rawText.length).trim(),
  };
}

export function getLegalDraftKind(text: string): LegalDraftKind | null {
  const taggedDraft = extractTaggedLegalDraft(text);
  if (taggedDraft) return taggedDraft.kind;

  const normalized = normalizeDraftText(text);
  if (!normalized) return null;

  const hasAiDocumentDisclaimer = /\[AI-Generated Document:/i.test(normalized);
  const looksLikeMemo = /^\s*(MEMORANDUM|MEMO)\b/im.test(normalized)
    || (/^\s*TO:\s+/im.test(normalized) && /^\s*RE:\s+/im.test(normalized));
  const looksLikeLetter = /^\s*Dear\s+.+,/im.test(normalized)
    || /\b(Sincerely|Very truly yours|Respectfully submitted),?\s*$/im.test(normalized);
  const mentionsDraftDocument = /\b(draft|prepare|template)\b/i.test(normalized)
    && /\b(letter|memo|memorandum|pleading|motion|demand)\b/i.test(normalized);

  if (looksLikeMemo) return 'memo';
  if (looksLikeLetter) return 'letter';
  if (hasAiDocumentDisclaimer || mentionsDraftDocument) return 'draft';

  return null;
}

export function isLegalDraftText(text: string): boolean {
  return getLegalDraftKind(text) !== null;
}

export function getLegalDraftTitle(text: string, kind: LegalDraftKind): string {
  const normalized = normalizeDraftText(text);
  const heading = normalized
    .split('\n')
    .map(line => line.trim())
    .find(line => {
      if (!line) return false;
      if (/^\[AI-Generated Document:/i.test(line)) return false;
      if (/^#+\s+/.test(line)) return true;
      if (/^(MEMORANDUM|MEMO)$/i.test(line)) return true;
      if (/^RE:\s+/i.test(line)) return true;
      return false;
    });

  if (heading) {
    return heading.replace(/^#+\s+/, '').replace(/^RE:\s*/i, '').slice(0, 96);
  }

  if (kind === 'letter') return 'Letter Draft';
  if (kind === 'memo') return 'Memo Draft';
  return 'Legal Draft';
}

export function createLegalDraftArtifact(text: string, messageIndex: number): LegalDraftArtifact | null {
  const taggedDraft = extractLegalDraft(text);
  if (!taggedDraft) return null;
  const draftText = taggedDraft.text;

  return {
    id: `draft-${messageIndex}`,
    messageIndex,
    kind: taggedDraft.kind,
    title: taggedDraft.title && !RETIRED_ARTIFACT_TITLE_PATTERN.test(taggedDraft.title)
      ? taggedDraft.title
      : getLegalDraftTitle(draftText, taggedDraft.kind),
    text: draftText,
  };
}

export function getLegalDraftDismissKey(artifact: LegalDraftArtifact): string {
  const normalizedText = normalizeDraftText(artifact.text).replace(/\s+/g, ' ');
  return `${artifact.kind}:${hashDraftText(normalizedText)}`;
}
